import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const LINK_RE = /\[\[link:(\/app\/[a-z0-9/-]*)\|([^\]|]{1,60})\]\]/gi;

export const splitLinks = (t) => {
  const links = [];
  const text = (t || "").replace(LINK_RE, (_, path, label) => {
    if (!links.some(l => l.path === path)) links.push({ path, label: label.trim() });
    return "";
  }).replace(/\n{3,}/g, "\n\n").trim();
  return { text, links: links.slice(0, 3) };
};

export const ChatLinks = ({ links, onNavigate }) => {
  const nav = useNavigate();
  if (!links?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="chat-deep-links">
      {links.map(l => (
        <button key={l.path} type="button" onClick={() => { nav(l.path); onNavigate?.(); }} data-testid={`chat-deep-link-${l.path.replace(/\//g, "-").replace(/^-/, "")}`}
          className="text-[10px] tracking-widest uppercase px-2.5 py-1.5 rounded-full border border-[#83B68B]/50 text-[#83B68B] hover:bg-[#83B68B] hover:text-white transition-colors inline-flex items-center gap-1">
          {l.label} <ArrowUpRight className="w-3 h-3" />
        </button>
      ))}
    </div>
  );
};

const normalise = (t) => (t || "")
  .replace(/^[ \t]*[•·▪◦][ \t]+/gm, "- ")
  .replace(/([^\n])\n(?=[ \t]*(?:[-*]|\d+[.)])[ \t]+)/g, "$1\n\n");

const components = {
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-0.5 last:mb-0">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-0.5 last:mb-0">{children}</ol>,
  li: ({ children }) => <li className="leading-snug">{children}</li>,
  h1: ({ children }) => <div className="font-semibold text-white mb-1">{children}</div>,
  h2: ({ children }) => <div className="font-semibold text-white mb-1">{children}</div>,
  h3: ({ children }) => <div className="font-semibold text-white mb-1">{children}</div>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noreferrer" className="text-[#83B68B] underline">{children}</a>,
  code: ({ children }) => <code className="px-1 py-0.5 rounded bg-white/10 text-[12px] font-mono">{children}</code>,
  hr: () => <hr className="my-2 border-white/10" />,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-[#83B68B]/60 pl-2 my-2 text-white/75">{children}</blockquote>,
  table: ({ children }) => <div className="overflow-x-auto mb-2"><table className="text-xs border-collapse">{children}</table></div>,
  th: ({ children }) => <th className="border border-white/15 px-2 py-1 text-left font-semibold">{children}</th>,
  td: ({ children }) => <td className="border border-white/15 px-2 py-1">{children}</td>,
};

export const ChatMarkdown = ({ text }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={components}>{normalise(text)}</ReactMarkdown>
);
